import React, { useState, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { adminApi } from '../../../api';

function buildRows(nodes, edges) {
  const children = {};
  const inDegree = {};
  nodes.forEach((n) => {
    children[n.id] = [];
    inDegree[n.id] = 0;
  });
  edges.forEach((e) => {
    if (children[e.from_node_id]) children[e.from_node_id].push(e.to_node_id);
    if (inDegree[e.to_node_id] !== undefined) inDegree[e.to_node_id] += 1;
  });

  const root = nodes.find((n) => inDegree[n.id] === 0);
  if (!root) return [];

  const depth = {};
  const queue = [root.id];
  depth[root.id] = 0;
  while (queue.length) {
    const curr = queue.shift();
    (children[curr] || []).forEach((childId) => {
      if (depth[childId] === undefined) {
        depth[childId] = depth[curr] + 1;
        queue.push(childId);
      }
    });
  }

  // All terminal nodes except head are leaves — force them to the same bottom row
  const maxNonLeafDepth = Math.max(
    0,
    ...nodes
      .filter((n) => !n.terminal_label || n.terminal_label === 'head')
      .filter((n) => depth[n.id] !== undefined)
      .map((n) => depth[n.id])
  );
  nodes.forEach((n) => {
    if (n.terminal_label && n.terminal_label !== 'head') {
      depth[n.id] = maxNonLeafDepth + 1;
    }
  });

  const levels = {};
  nodes.forEach((n) => {
    const d = depth[n.id] !== undefined ? depth[n.id] : 0;
    if (!levels[d]) levels[d] = [];
    levels[d].push(n);
  });

  return Object.keys(levels)
    .sort((a, b) => Number(a) - Number(b))
    .map((d) => levels[d]);
}

function NodeCard({ node, t }) {
  return (
    <Card className="border-secondary overflow-hidden text-muted" style={{ width: 320 }}>
      <Card.Body className="p-2">
        <table className="w-100 small">
          <tbody>
            {node.label && (
              <tr>
                <td className="text-muted pe-2 text-nowrap align-top">
                  {t('common:transactions.detail.graph.nodeLabel')}
                </td>
                <td className="fw-semibold text-break">{node.label}</td>
              </tr>
            )}
            {node.source_label && node.destination_label && (
              <tr>
                <td className="text-muted pe-2 text-nowrap align-top">
                  {t('common:transactions.detail.graph.route')}
                </td>
                <td className="text-break">
                  {node.source_label} &rarr; {node.destination_label}
                </td>
              </tr>
            )}
            {node.terminal_label === 'head' && (
              <tr className="text-center d-flex flex-column align-items-center">
                <td className="fw-semibold text-break">
                  {t('common:transactions.detail.graph.transactionCreated')}
                </td>
              </tr>
            )}
            {node.terminal_label && node.terminal_label !== 'head' && (
              <tr className="text-center d-flex flex-column align-items-center">
                <td className="fw-semibold text-break">{node.terminal_label}</td>
              </tr>
            )}
          </tbody>
        </table>
      </Card.Body>
    </Card>
  );
}

function GraphGrid({ graph, nodeDetails }) {
  const { t } = useTranslation();
  const { nodes } = graph;
  const rows = buildRows(nodes, graph.edges);

  return (
    <div>
      {(graph.label || graph.version) && (
        <div className="d-flex flex-column align-items-center gap-1 mb-3 text-primary">
          {graph.label && (
            <span>
              <span className="fw-semibold text-muted">
                {t('common:transactions.detail.graph.graph')}:{' '}
              </span>
              {graph.label}
            </span>
          )}
          {graph.version && (
            <span>
              <span className="fw-semibold text-muted">
                {t('common:transactions.detail.graph.version')}:{' '}
              </span>
              {graph.version}
            </span>
          )}
        </div>
      )}
      {rows.map((rowNodes, i) => (
        <div key={rowNodes.map((n) => n.id).join('-')}>
          {i > 0 && <div className="d-flex justify-content-center mb-2 text-muted">↓</div>}
          <div className="d-flex justify-content-center flex-wrap mb-2 gap-3">
            {rowNodes.map((node) => (
              <NodeCard
                key={node.id}
                node={node}
                details={nodeDetails[String(node.id)] || null}
                t={t}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TransactionGraph({ transactionId }) {
  const { t } = useTranslation();
  const [graph, setGraph] = useState(null);
  const [txState, setTxState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notTps, setNotTps] = useState(false);
  const [error, setError] = useState(false);

  const fetchGraph = async () => {
    try {
      const response = await adminApi.getTransactionGraph(transactionId);
      if (response.status_code === 404) {
        setNotTps(true);
      } else if (response.success) {
        setGraph(response.graph);
        setTxState(response.state || null);
      } else {
        setError(true);
      }
    } catch (err) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGraph();
  }, [transactionId]);

  if (isLoading)
    return <p className="text-muted">{t('common:transactions.detail.graph.loading')}</p>;
  if (notTps) return <p className="text-muted">{t('common:transactions.detail.graph.notTps')}</p>;
  if (error || !graph)
    return <p className="text-warning">{t('common:transactions.detail.graph.error')}</p>;

  return <GraphGrid graph={graph} nodeDetails={txState?.node_details || {}} />;
}

export default TransactionGraph;
